import { motion, useTransform } from "motion/react";
import { memo, useEffect, useRef } from "react";

import styled from "styled-components";

import CustomScrollbar from "UI/FnrCustomScrollbar/CustomScrollbar";
import FnrErrorBoundary from "UI/FnrErrorBoundary";

import useBackgroundManagerHeight from "Utilities/Hooks/useBackgroundManagerHeight";
import { hydrateFromCache } from "Utilities/ReactQuery/queryCache";
import { queryClient } from "Utilities/ReactQuery/queryClient";

import AccountAndSettings from "./AccountAndSettings";
import Giving from "./Giving";
import GroupCommunication from "./GroupCommunication";
import HouseholdDatabase from "./HouseholdDatabase";
import MinistryTools from "./MinistryTools";
import NavPanel from "./NavPanel";
import SignupsAndEvents from "./SignupsAndEvents";
import TopArea from "./TopArea";
import CloseBackground from "./components/CloseBackground";
import {
  useInitActiveSectionKey,
  useInitActiveSectionsNumber,
  useNavBarWrapperRef,
  useSetNavIsScrollable,
} from "./navBarStore";
import {
  useAnimatedBgManagerOffset,
  useCanSeeGiving,
  useCanSeeHouseholdDatabase,
  useCanSeeMinistryTools,
  useCanSeeSignupsNav,
  useExportedNavWidth,
  useIsFullScreen,
  useManageCollapseStateOnResize,
} from "./stateHooks";
import { outerPaddingExpanded } from "./styleConstants";

const navBarScrollbarColors = {
  color: "rgba(255,255,255,0.3)",
  backgroundColor: "transparent",
};

const navBarTrackInset = { top: 12, bottom: 12 };

const MainSections = memo(() => {
  const tools = useCanSeeMinistryTools();
  const database = useCanSeeHouseholdDatabase();
  const giving = useCanSeeGiving();
  const signups = useCanSeeSignupsNav();

  return (
    <MainSectionWrapper>
      <GroupCommunication />
      {database && <HouseholdDatabase />}
      {giving && <Giving />}
      {signups && <SignupsAndEvents />}
      {tools && <MinistryTools />}
    </MainSectionWrapper>
  );
});

const NavBarContents = () => {
  const isFullScreen = useIsFullScreen();
  const navBarWrapperRef = useNavBarWrapperRef();
  const setNavIsScrollable = useSetNavIsScrollable();

  const bgOffsetPx = useAnimatedBgManagerOffset();
  const fullScreenPx = isFullScreen ? 24 : 0;
  const height = useTransform(
    bgOffsetPx,
    (offset) => `calc(100% - ${fullScreenPx}px - ${offset})`
  );

  const hydrated = useRef(false);
  if (!hydrated.current) {
    hydrated.current = true;
    hydrateFromCache(queryClient);
  }

  useManageCollapseStateOnResize();
  useExportedNavWidth();
  useInitActiveSectionKey();
  useInitActiveSectionsNumber();

  const bgManagerHeight = useBackgroundManagerHeight();
  const bgManagerHeightRef = useRef(bgManagerHeight);
  bgManagerHeightRef.current = bgManagerHeight;
  const wasScrollableRef = useRef(false);

  // Track whether the nav's scroll container has overflowing content.
  // Observes both the container and its inner content wrapper for size changes.
  // Hysteresis: once scrollable, stay scrollable until content fits with
  // (bgManagerHeight + 8)px headroom — the same amount the container shrinks
  // when scrollable + bg panel open. Without this, removing the shrink grows
  // clientHeight, flipping the check back to false, which un-shrinks, and so on.
  useEffect(() => {
    const el = navBarWrapperRef.current;
    if (!el) return;

    const check = () => {
      const overflow = el.scrollHeight - el.clientHeight;
      const next = wasScrollableRef.current
        ? overflow > -(bgManagerHeightRef.current + 8)
        : overflow > 0;
      wasScrollableRef.current = next;
      setNavIsScrollable(next);
    };

    const ro = new ResizeObserver(check);
    ro.observe(el);
    const inner = el.firstElementChild;
    if (inner) ro.observe(inner);

    check();

    return () => ro.disconnect();
  }, [navBarWrapperRef, setNavIsScrollable]);

  return (
    <NavPanel>
      <NavBarWrapper $isFullScreen={isFullScreen} style={{ height }}>
        <CustomScrollbar
          ref={navBarWrapperRef}
          colors={navBarScrollbarColors}
          trackInset={navBarTrackInset}
        >
          <FnrErrorBoundary boundaryName="NavBar-TopArea">
            <TopArea isNavToolbar />
          </FnrErrorBoundary>
          <FnrErrorBoundary boundaryName="NavBar-MainSections">
            <MainSections />
          </FnrErrorBoundary>
          <FnrErrorBoundary boundaryName="NavBar-AccountAndSettings">
            <AccountAndSettings />
          </FnrErrorBoundary>
        </CustomScrollbar>
      </NavBarWrapper>
    </NavPanel>
  );
};

const MainSectionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  flex-grow: 1;
`;

const NavBarWrapper = styled(motion.div)`
  border-radius: 18px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: absolute;
  padding: ${outerPaddingExpanded}px;
  min-height: 0;
  width: calc(100% - ${({ $isFullScreen }) => ($isFullScreen ? 24 : 16)}px);
`;

const NavBar = () => {
  return (
    <>
      <CloseBackground />
      <NavBarContents />
    </>
  );
};

export default NavBar;
